import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { eur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, MonthSelector, Kpi, Card, thisMonth } from "@/components/common";
import TargetAlert from "@/components/TargetAlert";
import NightsGap from "@/components/NightsGap";
import MissingDataAlert from "@/components/MissingDataAlert";
import ExpensesReminder from "@/components/ExpensesReminder";
import StockAlert from "@/components/StockAlert";
import { Bell, AlertTriangle, ArrowRight, CreditCard } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid } from "recharts";

const monthName = (m) =>
  new Date(m + "-01").toLocaleDateString("it-IT", { month: "long", year: "numeric" });

export default function Dashboard() {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);
  const [report, setReport] = useState(null);
  const navigate = useNavigate();
  const { isManager } = useAuth();

  const loadDash = () => api.get(`/dashboard?month=${month}`).then((r) => setData(r.data));
  useEffect(() => {
    loadDash();
    api.get(`/report?month=${month}`).then((r) => setReport(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month]);

  const spese = report?.spese;

  return (
    <div>
      <PageHeader title="Dashboard" subtitle={`Panoramica · ${monthName(month)}`}>
        <MonthSelector value={month} onChange={setMonth} />
      </PageHeader>

      <MissingDataAlert />
      <StockAlert />
      <TargetAlert />
      <NightsGap />
      <ExpensesReminder />

      {data?.reminders?.length > 0 && (
        <div className="mb-6 space-y-2" data-testid="reminders">
          {data.reminders.map((r, i) => (
            <div key={i} className={`flex items-center gap-2 text-sm rounded-md border px-3 py-2 ${r.urgent ? "bg-red-50 border-red-200 text-red-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
              <Bell size={15} /> <b>{r.guest}</b> — {r.message}
            </div>
          ))}
        </div>
      )}

      {(data?.option_counts?.expired > 0 || data?.option_counts?.expiring > 0) && (
        <div className="mb-6 space-y-2" data-testid="option-banner">
          {data.option_counts.expired > 0 && (
            <div className="flex items-center justify-between gap-3 rounded-md border border-rose-200 bg-rose-50 px-4 py-3">
              <div className="flex items-center gap-2 text-sm text-rose-700"><AlertTriangle size={16} /> <b>Attenzione:</b> {data.option_counts.expired} opzioni scadute</div>
              <button data-testid="go-options-expired" onClick={() => navigate("/opzioni?f=scadute")} className="shrink-0 text-xs font-semibold px-3 py-1.5 rounded-md bg-rose-600 text-white hover:bg-rose-700 inline-flex items-center gap-1">Apri Opzioni prenotazione <ArrowRight size={13} /></button>
            </div>
          )}
          {data.option_counts.expiring > 0 && (
            <div className="flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
              <div className="flex items-center gap-2 text-sm text-amber-700"><AlertTriangle size={16} /> <b>Attenzione:</b> {data.option_counts.expiring} opzioni in scadenza</div>
              <button data-testid="go-options-expiring" onClick={() => navigate("/opzioni?f=scadenza")} className="shrink-0 text-xs font-semibold px-3 py-1.5 rounded-md bg-amber-500 text-white hover:bg-amber-600 inline-flex items-center gap-1">Apri Opzioni prenotazione <ArrowRight size={13} /></button>
            </div>
          )}
        </div>
      )}

      {(data?.payment_counts?.overdue > 0 || data?.payment_counts?.upcoming > 0) && (
        <div className="mb-6 space-y-2" data-testid="payment-banner">
          {data.payment_counts.overdue > 0 && (
            <div data-testid="pay-overdue" className="flex items-center justify-between gap-3 rounded-md border border-rose-200 bg-rose-50 px-4 py-3">
              <div className="flex items-center gap-2 text-sm text-rose-700"><CreditCard size={16} /> <b>Attenzione:</b> {data.payment_counts.overdue} pagamenti scaduti</div>
              {!isManager && <button data-testid="go-pay-overdue" onClick={() => navigate("/acconti-saldi?f=scaduti")} className="shrink-0 text-xs font-semibold px-3 py-1.5 rounded-md bg-rose-600 text-white hover:bg-rose-700 inline-flex items-center gap-1">Apri Acconti / Saldi <ArrowRight size={13} /></button>}
            </div>
          )}
          {data.payment_counts.upcoming > 0 && (
            <div data-testid="pay-upcoming" className="flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
              <div className="flex items-center gap-2 text-sm text-amber-700"><CreditCard size={16} /> <b>Attenzione:</b> {data.payment_counts.upcoming} pagamenti in scadenza</div>
              {!isManager && <button data-testid="go-pay-upcoming" onClick={() => navigate("/acconti-saldi?f=scadenza")} className="shrink-0 text-xs font-semibold px-3 py-1.5 rounded-md bg-amber-500 text-white hover:bg-amber-600 inline-flex items-center gap-1">Apri Acconti / Saldi <ArrowRight size={13} /></button>}
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 mb-6">
        <Kpi testid="kpi-camere" label="Camere" value={data?.camere ?? "—"} />
        <Kpi testid="kpi-arrivi" label="Arrivi" value={data?.arrivi ?? "—"} accent="text-sky-700" />
        <Kpi testid="kpi-partenze" label="Partenze" value={data?.partenze ?? "—"} accent="text-amber-600" />
        <Kpi testid="kpi-presenze" label="Presenze oggi" value={data?.presenze ?? "—"} accent="text-emerald-600" />
        <Kpi testid="kpi-cancellate" label="Cancellate" value={data?.cancellate ?? "—"} accent="text-red-500" />
        <Kpi testid="kpi-entrate" label="Entrate" value={eur(data?.entrate)} accent="text-slate-900" />
        <Kpi testid="kpi-cash" label="Cash incassato" value={eur(data?.cash_collected)} accent="text-teal-600" />
        <Kpi
          testid="kpi-netto"
          label="Netto"
          value={eur(data?.netto)}
          accent={data?.netto >= 0 ? "text-emerald-600" : "text-red-600"}
        />
      </div>

      {data?.occupancy?.per_property?.length > 0 && (
        <Card className="p-5 mb-6">
          <h3 className="font-heading font-bold text-slate-900 mb-1">Occupancy camere per proprietà</h3>
          <p className="text-xs text-slate-400 mb-4">{monthName(data.occupancy.month)} · {data.occupancy.days} giorni</p>
          <div className="space-y-3" data-testid="occupancy-per-property">
            {data.occupancy.per_property.map((o) => {
              const pct = Math.min(100, o.occupancy);
              const color = pct >= 75 ? "bg-emerald-500" : pct >= 40 ? "bg-amber-400" : "bg-sky-400";
              return (
                <div key={o.structure} data-testid={`occ-${o.structure}`}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium text-slate-700">{o.structure} <span className="text-slate-400 font-normal">· {o.rooms} camere</span></span>
                    <span className="tabular font-semibold text-slate-900">{o.occupancy}% <span className="text-xs text-slate-400 font-normal">({o.booked_nights}/{o.available_nights} notti)</span></span>
                  </div>
                  <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                    <div className={`h-full ${color} rounded-full transition-all`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {data?.annual?.length > 0 && (
        <Card className="p-5 mb-6">
          <h3 className="font-heading font-bold text-slate-900 mb-4">Andamento annuale · previsionale vs incassato</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="annual-forecast">
            {data.annual.map((a) => {
              const pct = a.previsionale > 0 ? Math.min(100, Math.round((a.effettivo / a.previsionale) * 100)) : 0;
              return (
                <div key={a.year} className="rounded-lg border border-slate-200 p-4" data-testid={`annual-${a.year}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-heading font-bold text-lg text-slate-900">{a.year}</span>
                    <span className="text-xs text-slate-400">{pct}% incassato</span>
                  </div>
                  <div className="flex justify-between text-sm py-1">
                    <span className="text-slate-500">Previsionale</span>
                    <span className="tabular font-semibold text-slate-900" data-testid={`annual-prev-${a.year}`}>{eur(a.previsionale)}</span>
                  </div>
                  <div className="flex justify-between text-[11px] text-slate-400 -mt-0.5 mb-0.5">
                    <span>di cui confermate {eur(a.confermato)}</span>
                    <span>opzionate {eur(a.opzionato)}</span>
                  </div>
                  <div className="flex justify-between text-sm py-1">
                    <span className="text-slate-500">Incassato ad oggi</span>
                    <span className="tabular font-semibold text-emerald-600" data-testid={`annual-eff-${a.year}`}>{eur(a.effettivo)}</span>
                  </div>
                  <div className="flex justify-between text-sm py-1 border-t border-slate-100 mt-1 pt-1.5">
                    <span className="text-slate-500">Residuo da incassare</span>
                    <span className="tabular font-semibold text-amber-600" data-testid={`annual-res-${a.year}`}>{eur(a.residuo)}</span>
                  </div>
                  <div className="flex justify-between text-sm py-1">
                    <span className="text-slate-500">Netto previsto</span>
                    <span className={`tabular font-semibold ${a.netto >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid={`annual-netto-${a.year}`}>{eur(a.netto)}</span>
                  </div>
                  <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-6 h-64" data-testid="annual-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.annual.map((a) => ({ anno: a.year, Previsionale: a.previsionale, Incassato: a.effettivo, Netto: a.netto }))} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
                <XAxis dataKey="anno" tick={{ fontSize: 12, fill: "#64748b" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} tickFormatter={(v) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : v)} width={40} />
                <Tooltip formatter={(v) => eur(v)} cursor={{ fill: "#f8fafc" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Previsionale" fill="#0ea5e9" radius={[4, 4, 0, 0]} maxBarSize={48} />
                <Bar dataKey="Incassato" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={48} />
                <Bar dataKey="Netto" fill="#f59e0b" radius={[4, 4, 0, 0]} maxBarSize={48} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <h3 className="font-heading font-bold text-slate-900 mb-4">Riepilogo spese</h3>
          {spese && (
            <div className="space-y-2 text-sm">
              {[
                ["Maldive Autentiche", spese.maldive_autentiche],
                ...Object.entries(spese.manual || {}),
                ["Affitti", spese.affitti],
                ["Staff", spese.staff],
                ["Spese Guesthouse", spese.guesthouse.total],
                ["Amministrazione", (spese.amministrazione?.total) || 0],
                ["MIRA", spese.mira],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between py-1.5 border-b border-slate-100">
                  <span className="text-slate-600">{k}</span>
                  <span className="tabular font-medium">{eur(v)}</span>
                </div>
              ))}
              <div className="flex justify-between pt-2 font-bold">
                <span>Totale spese</span>
                <span className="tabular text-red-600">{eur(spese.total)}</span>
              </div>
            </div>
          )}
        </Card>

        <Card className="p-5">
          <h3 className="font-heading font-bold text-slate-900 mb-4">Prenotazioni per origine</h3>
          <div className="space-y-2 text-sm">
            {report &&
              Object.entries(report.per_agency)
                .filter(([, v]) => v.count > 0)
                .map(([k, v]) => (
                  <div key={k} className="flex justify-between py-1.5 border-b border-slate-100">
                    <span className="text-slate-600">{k}</span>
                    <span className="tabular font-medium">
                      {v.count} pren. · {eur(v.entrate)}
                    </span>
                  </div>
                ))}
            {report && report.bookings_count === 0 && (
              <p className="text-slate-400 text-sm">Nessuna prenotazione nel mese.</p>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
